/**
 * @sunday/cart — the one basket. The Shopify cart is the only container; a bed is just a
 * grouping key on cart lines. No DOM here beyond one event.
 *
 * Line properties (handbook, "Cart and beds"):
 *   _bed          hidden   groups a mattress with its pillow, topper and protector  (bed1, our, par, kidr…)
 *   _bed_label    hidden   group heading in the cart ("Parents' room")
 *   _from_builder hidden   lines Build your bed may replace on "Update cart"
 *   Custom Size   visible  "58″ × 74″ (width × length)"; the variant is the smallest standard size containing it
 *
 * Which products are mattresses comes from the store (sunday_mattress entries), passed in by
 * the theme as window.Sunday.mattressProductIds. Nothing is named in code.
 *
 * Every change dispatches `sunday:cart` on document with { cart, added? }.
 */

import { t as T } from '@sunday/text';

const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

async function call(path, body) {
  const res = await fetch(root() + path, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.description || data.message || T('common.failed'));
    err.status = res.status;
    throw err;
  }
  return data;
}

function announce(cart, extra = {}) {
  document.dispatchEvent(new CustomEvent('sunday:cart', { detail: { cart, ...extra } }));
  return cart;
}

const mattressIds = () => ((window.Sunday && window.Sunday.mattressProductIds) || []).map(Number);

export const isMattress = (line) => mattressIds().includes(Number(line.product_id));
export const bedOf = (line) => (line.properties && line.properties._bed) || null;

/**
 * The cart, read with a no-op POST: GET /cart.js is redirected to the primary domain on the
 * theme preview (and fails there), while POST answers on any domain with the same cart JSON.
 */
export async function get() {
  return call('cart/update.js', { updates: {} });
}

/** Lines grouped by bed, in the order beds first appear. Lines without a bed go to "extras". */
export function beds(cart) {
  const out = new Map();
  for (const line of cart.items) {
    const key = bedOf(line) || 'extras';
    if (!out.has(key)) out.set(key, { key, label: (line.properties && line.properties._bed_label) || '', lines: [] });
    out.get(key).lines.push(line);
  }
  return [...out.values()];
}

/** A fresh bed key: bed1, bed2… not used yet in this cart. */
export function newBedKey(cart) {
  const used = new Set(cart.items.map(bedOf).filter(Boolean));
  let n = 1;
  while (used.has('bed' + n)) n++;
  return 'bed' + n;
}

/** The custom-size line property value, width first. */
export const customSizeText = (w, l) => `${w}″ × ${l}″ (width × length)`;

/**
 * Add several items in one call, so a bed lands whole or not at all.
 * items: [{ id, quantity, properties }]
 */
export async function addItems(items) {
  const added = await call('cart/add.js', { items });
  const cart = await get();
  return announce(cart, { added: added.items || [added] });
}

/**
 * Product-page add. A mattress starts a new bed (the added sheet then offers Replace / Keep both);
 * an accessory joins the first bed of the same standard size that lacks one, else "Extras";
 * with anyBed (a pillow, not sized to a bed) it joins the first bed that lacks one.
 * size: { w, l } of the standard variant (the one a custom size is cut from).
 * also: more variant ids added in the same call to the same bed (the mattress page's "Add both": its topper).
 */
export async function addFromProductPage({ variantId, productId, size, custom, anyBed = false, also = [] }) {
  const cart = await get();
  const mattress = mattressIds().includes(Number(productId));
  const properties = {};
  let replaceable = null;
  if (mattress) {
    const existing = cart.items.find(isMattress);
    properties._bed = newBedKey(cart);
    if (existing) replaceable = { line: existing, bed: bedOf(existing) };
  } else if (size) {
    const bed = beds(cart).find(
      (b) =>
        b.key !== 'extras' &&
        b.lines.some((l) => isMattress(l) && sameStandard(l, size)) &&
        !b.lines.some((l) => Number(l.product_id) === Number(productId))
    );
    if (bed) properties._bed = bed.key;
  } else if (anyBed) {
    // Not sized to a bed (a pillow): the first bed that has a mattress and none of these yet.
    const bed = beds(cart).find(
      (b) => b.key !== 'extras' && b.lines.some(isMattress) && !b.lines.some((l) => Number(l.product_id) === Number(productId))
    );
    if (bed) properties._bed = bed.key;
  }
  if (custom) properties['Custom Size'] = customSizeText(custom.w, custom.l);
  const more = also.map((id) => ({ id, quantity: 1, properties: properties._bed ? { _bed: properties._bed } : {} }));
  const result = await addItems([{ id: variantId, quantity: 1, properties }, ...more]);
  return { cart: result, replaceable, bed: properties._bed || null };
}

function sameStandard(line, size) {
  const t = line.variant_title || '';
  const m = /(\d+)\s*["″]?\s*[x×]\s*(\d+)/.exec(t);
  return !!m && Number(m[1]) === size.w && Number(m[2]) === size.l;
}

/**
 * Replace: the new mattress takes the old one's bed; the old mattress goes.
 * The same mattress and size twice ("Keep one"): the new line simply goes, the old bed stays as it was.
 * Otherwise the old line goes first, then the new line (found again: its key changes with its
 * properties) takes the old bed's _bed / _bed_label. Doing it in this order means the two lines can
 * never become identical and merge (which used to remove both).
 */
export async function replaceMattress({ newLineKey, oldLine }) {
  const cart = await get();
  const line = cart.items.find((l) => l.key === newLineKey);
  if (!line) return cart;
  if (Number(line.variant_id) === Number(oldLine.variant_id)) {
    return announce(await call('cart/change.js', { id: newLineKey, quantity: 0 }));
  }
  const props = { ...(line.properties || {}), _bed: bedOf(oldLine) };
  const label = oldLine.properties && oldLine.properties._bed_label;
  if (label) props._bed_label = String(label).trim();
  else delete props._bed_label;
  const after = await call('cart/change.js', { id: oldLine.key, quantity: 0 });
  const again = after.items.find((l) => l.key === newLineKey) || after.items.find((l) => Number(l.variant_id) === Number(line.variant_id) && bedOf(l) === bedOf(line));
  if (!again) return announce(after);
  const updated = await call('cart/change.js', { id: again.key, quantity: again.quantity, properties: props });
  return announce(updated);
}

/** Remove every line of one bed. Returns the removed lines (for Undo). */
export async function removeBed(key) {
  const cart = await get();
  const lines = cart.items.filter((l) => (bedOf(l) || 'extras') === key);
  const updates = Object.fromEntries(lines.map((l) => [l.key, 0]));
  const updated = await call('cart/update.js', { updates });
  announce(updated, { removed: lines });
  return lines;
}

/** Put removed lines back (Undo). */
export async function restore(lines) {
  return addItems(lines.map((l) => ({ id: l.variant_id, quantity: l.quantity, properties: l.properties || {} })));
}

/**
 * Build your bed: replace this bed's builder lines (and any other mattress in the same bed) with `items`.
 * Never two mattresses in one bed.
 */
export async function updateBed(key, items) {
  const cart = await get();
  const drop = cart.items.filter((l) => bedOf(l) === key && ((l.properties && l.properties._from_builder) || isMattress(l)));
  if (drop.length) await call('cart/update.js', { updates: Object.fromEntries(drop.map((l) => [l.key, 0])) });
  return addItems(items);
}

/** One line to a new quantity (0 takes it out). */
export async function setQty(key, quantity) {
  return announce(await call('cart/change.js', { id: key, quantity }));
}

/** Read the cart and tell the page (header count, In your cart buttons). */
export async function refresh() {
  return announce(await get());
}

/** Count of items, for the header badge. */
export const count = (cart) => cart.item_count;
